import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle, Check, Loader2, MapPin, Plus, X } from "lucide-react";
import { API, formatApiError } from "../auth/api";
import { todayStr } from "../lib/tourHelpers";
import DeliveryOptions, { optHours } from "./DeliveryOptions";

/**
 * Chaufføren opretter en tur (2026-10-23).
 *
 * Kunden (fx ARGO) bestiller i sit eget system (fx Pickup). Chaufføren ser
 * ordren og lægger den ind her - så den køres, prissættes, faktureres og
 * tæller i løn og overblik som alle andre ture. På en genbrugsplads-kundes
 * plads bliver det kundens bestilling (som "Bestil for kunden"); på firmaets
 * egen lokation en egen tur. Turen lægges på chaufførens liste med det samme.
 *
 * Kun det nødvendige: plads, materiale, container (altid - fra pladsens liste eller skrevet), tøm/byt og kundens
 * ordrenummer (eller hvem der bad om det). Adressen hentes fra kundens
 * kalender. Findes der allerede en åben bestilling på containeren, tilbydes
 * den i stedet (ingen dobbeltture). Backend: /api/driver/new-tour*.
 */
const chip = (on) => `px-3.5 h-11 rounded-xl text-sm font-semibold border flex items-center gap-1.5 ${on
  ? "bg-orange-500 border-orange-500 text-white" : "bg-white border-slate-200 text-slate-700 active:bg-slate-50"}`;
const inputCls = "w-full h-12 px-3.5 rounded-xl border border-slate-200 bg-white text-base outline-none focus:ring-2 focus:ring-orange-400/50";
const lbl = "block text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-1.5";
const detailOf = (e) => e?.response?.data?.detail;
const errText = (e, fb) => {
  const d = detailOf(e);
  if (d && typeof d === "object" && d.message) return d.message;
  return d != null ? formatApiError(e) : fb;
};
const fmtDay = (d) => (d === todayStr() ? "I dag" : new Date(`${d}T12:00:00`).toLocaleDateString("da-DK", { weekday: "short", day: "numeric", month: "short" }));

const TASK_WORD = { normal: "Tømning", flyt: "Byt", vaerksted: "Til værksted", afhent_vaerksted: "Hent fra værksted", loes_afhentning: "Løs afhentning" };

// 2026-10-25: Hver oplysning med sit navn - før stod kundens ordrenr. alene
// ("I dag · 43434") og blev læst som et containernummer.
function orderText(o) {
  const who = o.mine ? "på din liste" : o.taken_by ? `${o.taken_by} ${o.on_way ? "kører" : "har"} den`
    : o.assigned_to ? `på ${o.assigned_to}s liste` : o.on_way ? "undervejs" : "ikke taget endnu";
  return [`${TASK_WORD[o.tour_type] || "Bestilling"} ${fmtDay(o.date).toLowerCase()}`,
    o.container ? `container ${o.container}` : "",
    o.external_ref ? `ordrenr. ${o.external_ref}` : o.requested_by ? `bestilt af ${o.requested_by}` : "",
    o.fixed ? "fast tømning" : "", who].filter(Boolean).join(" · ");
}

function OpenOrderRow({ o, onTake, testid }) {
  return (
    <div className="flex items-center gap-2 rounded-lg bg-white/70 px-2.5 py-2">
      <span className="flex-1 min-w-0">{orderText(o)}</span>
      {o.can_take && <button className="h-9 px-3 rounded-lg bg-orange-500 text-white text-xs font-bold shrink-0" onClick={onTake} data-testid={testid}>Tag den</button>}
    </div>
  );
}

export default function DriverNewTourSheet({ open, onClose, reportDate, onCreated, onTake }) {
  const day = reportDate && reportDate >= todayStr() ? reportDate : todayStr();
  const [sites, setSites] = useState(null);       // {customer_name, own, sites[]} | {error}
  const [plads, setPlads] = useState("");
  const [opts, setOpts] = useState(null);
  const [f, setF] = useState({ material: "", container: "", containerNo: "", other: false, type: "normal", ref: "", noRef: false, asked: "", comment: "", address: "", deliveryId: "", ownAddress: false });
  const [check, setCheck] = useState(null);
  const [otherOk, setOtherOk] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = (patch) => setF((x) => ({ ...x, ...patch }));

  useEffect(() => {
    if (!open) return;
    setSites(null); setPlads(""); setOpts(null); setCheck(null);
    setF({ material: "", container: "", containerNo: "", other: false, type: "normal", ref: "", noRef: false, asked: "", comment: "", address: "", deliveryId: "", ownAddress: false });
    axios.get(`${API}/driver/new-tour/sites`)
      .then((r) => { setSites(r.data); if (r.data.sites?.length === 1) setPlads(r.data.sites[0].name); })
      .catch((e) => setSites({ error: errText(e, "Kunne ikke hente pladserne") }));
  }, [open]);

  useEffect(() => {
    setOpts(null);
    if (!open || !plads) return;
    axios.get(`${API}/driver/new-tour/options`, { params: { plads_name: plads } })
      .then((r) => setOpts(r.data)).catch((e) => setOpts({ error: errText(e, "Kunne ikke hente pladsens materialer"), materials: [] }));
  }, [open, plads]);

  const own = !!opts?.own;
  const containers = useMemo(() => (opts?.materials || []).find((m) => m.material === f.material)?.containers || [], [opts, f.material]);
  // 2026-10-28: Containeren skal altid vælges eller skrives - også på en egen lokation (det er en containertur).
  // Pladsens liste først; "Andet nr." er reserven. Vedligeholder firmaet listen, kommer et nyt nr. på den.
  const manualNo = f.other || containers.length === 0;
  const containerReady = manualNo ? !!f.containerNo.trim() : !!f.container;

  // Adresse (kundens kalender) og åbne bestillinger på containeren
  useEffect(() => {
    setCheck(null);
    setF((prev) => (prev.deliveryId ? { ...prev, deliveryId: "" } : prev));
    // 2026-10-27: Også på en egen lokation - dens kalender giver dagens anlæg (primær + alternativer).
    if (!open || !plads || !f.material || !containerReady || (own && f.type !== "normal")) return undefined;
    const t = setTimeout(() => {
      axios.get(`${API}/driver/new-tour/check`, { params: { plads_name: plads, material: f.material, tour_type: f.type, date: day,
        container_label: manualNo ? "" : f.container, container_no: manualNo ? f.containerNo.trim() : "" } })
        .then((r) => setCheck(r.data)).catch(() => setCheck(null));
    }, 350);
    return () => clearTimeout(t);
  }, [open, plads, f.material, f.container, f.containerNo, f.type, manualNo, own, containerReady, day]);

  // 2026-10-27: Dagens primære + alternativer (med egne tider) - chaufføren vælger et andet, fx når
  // det primære er lukket. Standard er dagens (er det primære lukket: det første åbne alternativ).
  const deliveryOpts = (check && f.type === "normal" && check.delivery_options) || [];
  const picked = deliveryOpts.find((o) => o.id === (f.deliveryId || check?.delivery_address_id)) || deliveryOpts.find((o) => o.primary) || null;
  const pickedHours = picked ? optHours(picked) : (check?.open_from ? `${check.open_from}–${check.open_to}` : "");
  const needAddress = !own && f.type === "normal" && check && (f.ownAddress || (!check.address && !picked));
  // 2026-10-25: Kun den samme tømning spærrer: samme opgave og samme dag (eller
  // en forsinket, der ikke er kørt, mod en ny til i dag). En anden opgave samme
  // dag er en advarsel (to biler til samme container?). En bestilling en anden
  // dag - fx en fast tømning eller en serie - er en anden tømning: kun oplysning.
  const allOpen = check?.open_orders || [];
  const sameDay = (o) => o.same_day !== false;
  const openOrders = allOpen.filter((o) => o.same_type !== false && sameDay(o));
  const otherOrders = allOpen.filter((o) => o.same_type === false && sameDay(o));
  // 2026-10-25: Startet en tidligere dag og aldrig afsluttet = sikkert en glemt
  // "Afslut". Den spærrer ikke (serveren er enig) - chaufføren får besked.
  const staleOrders = allOpen.filter((o) => !sameDay(o) && o.started_before);
  const laterOrders = allOpen.filter((o) => !sameDay(o) && !o.started_before);
  // Overskriften: "i dag" kun når bestillingen ER til i dag - en forsinket står med sin egen dag.
  const lateOpen = openOrders.some((o) => o.date && o.date < day);
  useEffect(() => { setOtherOk(false); }, [f.container, f.containerNo, f.material, f.type, plads]);
  const refOk = f.noRef ? !!f.asked.trim() : !!f.ref.trim();
  const ready = plads && f.material.trim() && containerReady && refOk && (!needAddress || f.address.trim()) && (!otherOrders.length || otherOk);

  const submit = async () => {
    if (!ready || saving) return;
    setSaving(true);
    try {
      const r = await axios.post(`${API}/driver/new-tour`, {
        plads_name: plads, material: f.material.trim(), tour_type: f.type, date: day,
        container_label: manualNo ? "" : f.container, container_no: manualNo ? f.containerNo.trim() : "",
        external_ref: f.noRef ? "" : f.ref.trim(), requested_by: f.noRef ? f.asked.trim() : "",
        comment: f.comment.trim(), custom_address: needAddress ? f.address.trim() : "",
        delivery_address_id: !needAddress && f.deliveryId ? f.deliveryId : "",
      });
      onCreated?.(r.data);
      onClose();
    } catch (e) {
      toast.error(errText(e, "Turen kunne ikke oprettes"));
    } finally { setSaving(false); }
  };

  if (!open) return null;
  const customer = sites?.customer_name || "";
  return createPortal(
    <div className="fixed inset-0 z-[85] flex items-end sm:items-center justify-center" data-testid="new-tour-sheet">
      <div className="absolute inset-0 bg-slate-900/50" onClick={onClose} />
      <div className="relative w-full sm:max-w-md bg-slate-50 rounded-t-3xl sm:rounded-2xl shadow-2xl max-h-[92vh] flex flex-col" style={{ paddingBottom: "max(12px, env(safe-area-inset-bottom))" }}>
        <div className="mx-auto mt-2 w-10 h-1.5 rounded-full bg-slate-200 sm:hidden" />
        <div className="flex items-start justify-between px-5 pt-3 pb-2">
          <div>
            <h3 className="text-lg font-extrabold text-slate-900">Ny tur</h3>
            <p className="text-xs text-slate-500">
              {sites?.own ? "Egen tur" : customer ? `Bliver ${customer}s bestilling` : ""} · {fmtDay(day)} · lægges på din liste
            </p>
          </div>
          <button onClick={onClose} className="p-2 -mr-2 text-slate-500" aria-label="Luk"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 space-y-4 pb-3">
          {!sites && <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>}
          {sites?.error && <div className="rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-sm px-3 py-3">{sites.error}</div>}

          {sites?.sites && (
            <div>
              <span className={lbl}>Plads</span>
              {sites.sites.length === 1 ? (
                <div className="flex items-center gap-2 rounded-xl bg-white border border-slate-200 px-3.5 py-3">
                  <MapPin className="w-4 h-4 text-slate-400" />
                  <div className="min-w-0"><div className="font-semibold text-slate-800 truncate">{sites.sites[0].name}</div><div className="text-xs text-slate-500 truncate">{sites.sites[0].address}</div></div>
                </div>
              ) : (
                <select className={inputCls} value={plads} onChange={(e) => { setPlads(e.target.value); set({ material: "", container: "", containerNo: "", other: false }); }} data-testid="new-tour-plads">
                  <option value="">Vælg plads…</option>
                  {sites.sites.map((p) => <option key={p.name} value={p.name}>{p.name}</option>)}
                </select>
              )}
              {sites.sites.length === 0 && <p className="text-sm text-slate-500 mt-1">Ingen pladser for kunden. Skift kunde øverst.</p>}
            </div>
          )}

          {plads && !opts && <div className="py-4 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>}
          {opts?.error && <div className="rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-sm px-3 py-3">{opts.error}</div>}

          {opts && !opts.error && (
            <>
              <div>
                <span className={lbl}>Materiale</span>
                {own ? (
                  <>
                    <input className={inputCls} value={f.material} onChange={(e) => set({ material: e.target.value })} placeholder="Fx Træ" data-testid="new-tour-material-input" />
                    {opts.materials.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {opts.materials.slice(0, 12).map((m) => <button key={m.material} type="button" className={chip(f.material === m.material)}
                          onClick={() => set({ material: m.material, container: "", containerNo: "", other: false })} data-testid={`new-tour-mat-${m.material}`}>{m.material}</button>)}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {opts.materials.map((m) => (
                      <button key={m.material} type="button" className={chip(f.material === m.material)} data-testid={`new-tour-mat-${m.material}`}
                        onClick={() => set({ material: m.material, container: "", containerNo: "", other: false })}>{m.material}</button>
                    ))}
                    {opts.materials.length === 0 && <p className="text-sm text-slate-500">Pladsen har ingen materialer for jeres firma. Kontakt planlæggeren.</p>}
                  </div>
                )}
              </div>

              {f.material && (
                <div>
                  <span className={lbl}>Container</span>
                  {containers.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {containers.map((c) => (
                        <button key={c.label} type="button" className={chip(!f.other && f.container === c.label)} onClick={() => set({ container: c.label, other: false })}>
                          {c.container_id ? `Nr. ${c.container_id}` : c.label}{c.capacity_label ? <span className="opacity-70 font-normal"> · {c.capacity_label}</span> : null}
                        </button>
                      ))}
                      <button type="button" className={chip(f.other)} onClick={() => set({ other: true, container: "" })}>Andet nr.</button>
                    </div>
                  )}
                  {manualNo && (
                    <>
                      <input className={`${inputCls} font-mono`} value={f.containerNo} onChange={(e) => set({ containerNo: e.target.value })}
                        placeholder="Containernummer (står på containeren)" inputMode="text" data-testid="new-tour-container-no" />
                      {opts.adds_new && f.containerNo.trim() && !containers.some((c) => c.container_id === f.containerNo.trim()) && (
                        <p className="text-[11px] text-slate-500 mt-1" data-testid="new-tour-adds-new">Nyt nr. - containeren kommer på pladsens liste, så den kan vælges næste gang.</p>
                      )}
                    </>
                  )}
                </div>
              )}

              <div>
                <span className={lbl}>Opgave</span>
                <div className="grid grid-cols-2 gap-1.5">
                  {[["normal", "Tøm container"], ["flyt", "Byt container"]].map(([k, l]) => (
                    <button key={k} type="button" className={`${chip(f.type === k)} justify-center`} onClick={() => set({ type: k })} data-testid={`new-tour-type-${k}`}>{l}</button>
                  ))}
                </div>
                {f.type === "flyt" && <p className="text-[11px] text-slate-500 mt-1.5">Byt = anden containertype på pladsen, uden tømning. Skal den tømmes, så vælg Tøm.</p>}
              </div>

              <div>
                {!f.noRef ? (
                  <>
                    <span className={lbl}>Kundens ordrenr. <span className="normal-case font-normal">(ikke containerens nr.)</span></span>
                    <input className={inputCls} value={f.ref} onChange={(e) => set({ ref: e.target.value })} placeholder="Fx Pickup 48213" data-testid="new-tour-ref" />
                    <button type="button" className="mt-1.5 text-xs font-semibold text-slate-500 underline" onClick={() => set({ noRef: true })}>Ingen ordrenr.?</button>
                  </>
                ) : (
                  <>
                    <span className={lbl}>Hvem bad om turen?</span>
                    <input className={inputCls} value={f.asked} onChange={(e) => set({ asked: e.target.value })} placeholder="Fx Jens på pladsen" data-testid="new-tour-asked" />
                    <button type="button" className="mt-1.5 text-xs font-semibold text-slate-500 underline" onClick={() => set({ noRef: false })}>Der er et ordrenr.</button>
                  </>
                )}
              </div>

              {check && f.type === "normal" && (check.address || picked) && !f.ownAddress && (
                <div className="rounded-xl bg-white border border-slate-200 px-3.5 py-2.5 text-sm space-y-2" data-testid="new-tour-address">
                  <div>
                    <span className="text-slate-500">Køres til </span>
                    <b className="text-slate-800">{picked ? `${picked.label}${picked.line ? `, ${picked.line}` : ""}` : `${check.delivery_name ? `${check.delivery_name}, ` : ""}${check.address}`}</b>
                    {pickedHours && <span className="text-slate-500"> · {pickedHours === "lukket" ? "lukket den dag" : `åben ${pickedHours}`}</span>}
                  </div>
                  {(picked ? picked.closed : check.is_closed) && <div className="text-amber-700 text-xs">{picked?.label || "Det primære"} er lukket {check.holiday_name ? `(${check.holiday_name})` : "den dag"}{deliveryOpts.length > 1 ? " – vælg et andet nedenfor" : " – tjek med planlæggeren"}</div>}
                  {check.primary_closed && !f.deliveryId && picked && !picked.closed && (
                    <div className="text-amber-700 text-xs" data-testid="new-tour-primary-closed">{check.primary_closed} er lukket den dag – {picked.label} er valgt.</div>
                  )}
                  <DeliveryOptions options={deliveryOpts} value={picked?.id || ""} testid="new-tour-delivery-options"
                    onPick={(o) => set({ deliveryId: o.id === check.delivery_address_id ? "" : o.id })} />
                  {!own && <button type="button" className="text-xs font-semibold text-slate-500 underline" onClick={() => set({ ownAddress: true })}>Anden adresse (kun denne tur)</button>}
                </div>
              )}
              {needAddress && (
                <div>
                  <span className={lbl}>Leveringsadresse</span>
                  <input className={inputCls} value={f.address} onChange={(e) => set({ address: e.target.value })} placeholder="Hvor skal containeren hen?" />
                </div>
              )}

              {openOrders.length > 0 && (
                <div className="rounded-xl bg-amber-50 border border-amber-200 px-3.5 py-3 text-sm text-amber-900 space-y-2" data-testid="new-tour-open-orders">
                  <div className="flex gap-2"><AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /><span>{lateOpen
                    ? `Containeren har en ${f.type === "flyt" ? "byt" : "tømning"}, der ikke er kørt endnu – opret den ikke igen.`
                    : `Containeren er allerede bestilt til ${f.type === "flyt" ? "byt" : "tømning"} ${fmtDay(day).toLowerCase()} – opret den ikke igen.`}</span></div>
                  {openOrders.map((o) => <OpenOrderRow key={o.tour_id} o={o} onTake={() => { onTake?.(o.tour_id); onClose(); }} testid="new-tour-take" />)}
                  {openOrders.some((o) => o.can_take) ? (
                    <p className="text-xs">{lateOpen ? "Den er ikke kørt endnu, så den er dagens tømning. " : ""}Tag den i stedet for at oprette en ny.</p>
                  ) : openOrders.some((o) => o.on_way && !o.mine) ? (
                    <p className="text-xs">En kollega kører den nu.</p>
                  ) : openOrders.some((o) => !o.mine) ? (
                    <p className="text-xs">Den står på en kollegas liste – ring til planlæggeren, hvis du skal køre den.</p>
                  ) : null}
                </div>
              )}

              {openOrders.length === 0 && otherOrders.length > 0 && (
                <div className="rounded-xl bg-amber-50 border border-amber-200 px-3.5 py-3 text-sm text-amber-900 space-y-2" data-testid="new-tour-other-orders">
                  <div className="flex gap-2"><AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /><span>Containeren har samme dag en åben bestilling med en anden opgave. Er det samme opgave, så tag den i stedet – ellers kører to biler.</span></div>
                  {otherOrders.map((o) => <OpenOrderRow key={o.tour_id} o={o} onTake={() => { onTake?.(o.tour_id); onClose(); }} />)}
                  <label className="flex items-center gap-2 font-semibold"><input type="checkbox" className="w-5 h-5 accent-orange-500" checked={otherOk} onChange={(e) => setOtherOk(e.target.checked)} data-testid="new-tour-other-ok" /> Det er en anden opgave – opret alligevel</label>
                </div>
              )}

              {openOrders.length === 0 && staleOrders.length > 0 && (
                <div className="rounded-xl bg-white border border-slate-200 px-3.5 py-2.5 text-xs text-slate-600 space-y-1" data-testid="new-tour-stale-orders">
                  {staleOrders.map((o) => (
                    <div key={o.tour_id}>
                      {o.taken_by || "En kollega"} startede en {TASK_WORD[o.tour_type]?.toLowerCase() || "tur"} af containeren {fmtDay(o.started_before).toLowerCase()} og har ikke afsluttet den – sikkert glemt.
                      Det er ikke dagens tømning, så du kan godt oprette turen. Kontoret ser den gamle tur som forsinket.
                    </div>
                  ))}
                </div>
              )}

              {openOrders.length === 0 && laterOrders.length > 0 && (
                <div className="rounded-xl bg-white border border-slate-200 px-3.5 py-2.5 text-xs text-slate-600 space-y-1" data-testid="new-tour-later-orders">
                  <div>Containeren er også bestilt en anden dag – det er en anden tømning, du kan godt oprette turen:</div>
                  {laterOrders.map((o) => <div key={o.tour_id} className="text-slate-500">{orderText(o)}</div>)}
                </div>
              )}

              <details className="text-sm">
                <summary className="cursor-pointer text-slate-500 font-semibold">Note til turen (valgfri, kunden ser den ikke)</summary>
                <textarea className={`${inputCls} h-20 py-2 mt-2`} value={f.comment} onChange={(e) => set({ comment: e.target.value })} placeholder="Fx står bag hallen" />
              </details>
            </>
          )}
        </div>

        <div className="px-5 pt-2">
          <button onClick={submit} disabled={!ready || saving || openOrders.length > 0}
            className="w-full py-3.5 rounded-2xl bg-orange-500 text-white text-base font-extrabold flex items-center justify-center gap-2 disabled:opacity-40" data-testid="new-tour-save">
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Plus className="w-5 h-5" />} Opret tur
          </button>
          {openOrders.length > 0 && ready && <p className="text-[11px] text-amber-700 text-center mt-1.5">Containeren har allerede en åben tur – se ovenfor</p>}
          {!ready && opts && !opts.error && (
            <p className="text-[11px] text-slate-500 text-center mt-1.5 flex items-center justify-center gap-1">
              <Check className="w-3 h-3" /> {!f.material ? "Vælg materiale" : !containerReady ? "Vælg containeren" : !refOk ? (f.noRef ? "Skriv hvem der bad om turen" : "Skriv kundens ordrenr.") : needAddress ? "Skriv leveringsadressen" : otherOrders.length && !otherOk ? "Tjek den åbne bestilling ovenfor" : ""}
            </p>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
